/**
 * Viewer Headers Forwarding Utility
 *
 * SSR 환경에서 들어온 요청의 클라이언트 정보(IP / User-Agent / Referer / 지역)를 읽어
 * 백엔드 API 호출 시 헤더로 전달합니다.
 *
 * - CSR 환경에서는 빈 객체를 반환합니다.
 * - `next/headers`가 없는 환경에서도 에러 없이 동작합니다.
 * - 환경변수 `DISABLE_CF_HEADER_FORWARDING=true`로 비활성화할 수 있습니다. (레거시 이름 유지)
 */

/**
 * Vercel 지역(geo) 헤더 → 백엔드 전달 헤더 매핑.
 * Vercel 엣지가 모든 배포에 주입하는 `x-vercel-ip-*` 헤더를 소스로 사용한다.
 * (city는 RFC3986 percent-encoding되어 오므로 별도 디코딩한다.)
 * is-mobile/is-desktop은 Vercel 대응 헤더가 없어 제외 — 백엔드가 dd-viewer-user-agent로 판별한다.
 */
const VERCEL_GEO_HEADER_MAP: Record<string, string> = {
  'x-vercel-ip-country': 'dd-viewer-country',
  'x-vercel-ip-country-region': 'dd-viewer-country-region',
  'x-vercel-ip-latitude': 'dd-viewer-latitude',
  'x-vercel-ip-longitude': 'dd-viewer-longitude',
  'x-vercel-ip-timezone': 'dd-viewer-timezone',
};

/**
 * SSR 환경에서 요청 헤더를 읽어 백엔드 API 전달용 헤더 객체를 반환합니다.
 * CSR 환경이나 next/headers를 사용할 수 없는 환경에서는 빈 객체를 반환합니다.
 */
export async function getViewerForwardHeaders(): Promise<
  Record<string, string>
> {
  if (typeof window !== 'undefined') {
    return {};
  }

  if (process.env.DISABLE_CF_HEADER_FORWARDING === 'true') {
    return {};
  }

  try {
    // biome-ignore lint/suspicious/noTsIgnore: next/headers is an optional peer dependency
    // @ts-ignore -- next/headers is an optional peer dependency
    const mod = 'next/headers';
    const { headers } = await import(mod);
    const headerStore = await headers();

    const result: Record<string, string> = {};

    // 실 클라이언트 IP → x-forwarded-for
    // Vercel은 incoming x-forwarded-for 첫 토큰에 실 클라이언트 공개 IP를 넣어준다.
    // 백엔드 앞 CloudFront는 뒤에 hop을 append하므로, 백엔드는 첫 토큰을 읽으면 된다.
    const forwardedFor = headerStore.get('x-forwarded-for');
    if (forwardedFor) {
      result['x-forwarded-for'] = forwardedFor.split(',')[0].trim();
    }

    // User-Agent → dd-viewer-user-agent (fetch 기본 UA와 충돌 방지)
    const userAgent = headerStore.get('user-agent');
    if (userAgent) {
      result['dd-viewer-user-agent'] = userAgent;
    }

    // Referer → dd-viewer-referer
    const referer = headerStore.get('referer');
    if (referer) {
      result['dd-viewer-referer'] = referer;
    }

    // Vercel 지역(geo) 헤더 → dd-viewer-*
    for (const [vercelHeader, ddHeader] of Object.entries(
      VERCEL_GEO_HEADER_MAP
    )) {
      const value = headerStore.get(vercelHeader);
      if (value) {
        result[ddHeader] = value;
      }
    }

    // city는 percent-encoding(RFC3986)된 원본 그대로 전달한다.
    // HTTP 헤더 값은 ASCII만 허용되므로, 여기서 decodeURIComponent로 디코딩하면
    // 한글 등 non-ASCII 문자가 헤더에 들어가 fetch(undici)에서
    // "invalid header value"로 throw된다. (PE-213 이전 cloudfront 동작과 동일하게
    // 원본 전달, 디코딩은 백엔드가 수행)
    const city = headerStore.get('x-vercel-ip-city');
    if (city) {
      result['dd-viewer-city'] = city;
    }

    return result;
  } catch {
    return {};
  }
}
